/**
 * Shared HTTP helper for the REST API surface.
 *
 * The browser client speaks REST against `/api/<resource>` via
 * TanStack Query (see CLAUDE.md "Data fetching"). All `fetch*`
 * functions in `api/*.ts` route through `apiFetch` so error shape +
 * cookie semantics + JSON parsing stay consistent.
 *
 * Server responses follow the convention:
 *   - 2xx: JSON body (the resource shape — see each route module)
 *   - 4xx/5xx: `{ "error": "<code>", "message": "<human>" }`
 *
 * `apiFetch` throws `ApiError` (carries status + parsed body) on
 * non-2xx; TanStack Query surfaces this through `error` / catches it
 * on mutations so `onError` callbacks see the typed shape.
 */

export interface ApiErrorBody {
  error?: string
  message?: string
  field?: string
}

export class ApiError extends Error {
  status: number
  body: ApiErrorBody
  code: string | null

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.code = body.error ?? null
  }
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** JSON body — serialized via `JSON.stringify`. Omit for GET. */
  body?: unknown
  /** Query string params; serialized in stable insertion order. */
  query?: Record<string, string | number | boolean | null | undefined>
  /** AbortSignal forwarded to fetch — TanStack Query supplies one. */
  signal?: AbortSignal
}

/**
 * Plan 25 — read the persisted kiosk bearer token. The /pair page
 * stores the plaintext token in localStorage under this key; if
 * present, every subsequent REST call from this client sends it as
 * `Authorization: Kiosk <token>` so the server treats the request as
 * kiosk-authenticated (per Plan 22's bearer middleware).
 *
 * Returns null when not running in a browser (tests / SSR) or when
 * the key is unset / empty.
 */
export const KIOSK_TOKEN_STORAGE_KEY = 'kioskToken'
export const KIOSK_BROWSER_ID_STORAGE_KEY = 'ordoBrowserId'

/**
 * Per-tab impersonation ("Access school/chef view"). The impersonation callback
 * hands the signed session + banner tokens to the tab via a URL fragment; the
 * `/impersonate/land` page persists them here. Because they live in
 * sessionStorage (not localStorage / cookies) they are isolated to this tab and
 * destroyed when the tab closes. Every REST call re-sends them as headers so the
 * server resolves this tab as the impersonated org — see `apiFetch` below.
 */
export const IMPERSONATION_SESSION_STORAGE_KEY = '__o_i_imp_s'
export const IMPERSONATION_BANNER_STORAGE_KEY = '__o_i_imp_b'

function readSessionStorageValue(key: string): string | null {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return null
  }
  try {
    const v = sessionStorage.getItem(key)
    return v && v.length > 0 ? v : null
  } catch {
    return null
  }
}

export function readImpersonationSession(): string | null {
  return readSessionStorageValue(IMPERSONATION_SESSION_STORAGE_KEY)
}

export function readImpersonationBanner(): string | null {
  return readSessionStorageValue(IMPERSONATION_BANNER_STORAGE_KEY)
}

export function clearImpersonationTokens(): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return
  }
  try {
    sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY)
    sessionStorage.removeItem(IMPERSONATION_BANNER_STORAGE_KEY)
  } catch {
    // ignore — storage may be unavailable (private mode, etc.)
  }
}

export function readKioskToken(): string | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null
  }
  try {
    const v = localStorage.getItem(KIOSK_TOKEN_STORAGE_KEY)
    return v && v.length > 0 ? v : null
  } catch {
    return null
  }
}

function readKioskBrowserId(): string | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null
  }
  try {
    const v = localStorage.getItem(KIOSK_BROWSER_ID_STORAGE_KEY)
    return v && v.length > 0 ? v : null
  } catch {
    return null
  }
}

/**
 * Append `?k=v&...` to a URL, skipping null/undefined values. Booleans
 * are stringified (true/false). Numbers → strings.
 */
export function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  if (!query) {
    return path
  }
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) {
      continue
    }
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs.length === 0 ? path : `${path}?${qs}`
}

/**
 * Thin fetch wrapper:
 *   - includes credentials (cookie-based session)
 *   - serializes JSON body + sets Content-Type
 *   - parses JSON response; throws `ApiError` on non-2xx
 *   - hands the `AbortSignal` from TanStack Query through to fetch
 *     so unmounting components cancel in-flight requests
 */
/**
 * Kiosk + impersonation headers every authenticated request must carry.
 *
 * Shared by `apiFetch` and `apiFetchBlob`: these tokens live in
 * localStorage/sessionStorage rather than cookies, so ANY request that skips
 * this block (a plain `<a href>` or `window.open` download, for instance)
 * resolves as the real user instead of the impersonated org.
 */
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  // Plan 25 — when a kiosk token is persisted in localStorage (set by
  // the /pair page on a paired tablet), send it as the Authorization
  // header so the server's kiosk-auth middleware treats the request
  // as kiosk-paired. The session-cookie path is still tried as a
  // fallback by the server, but a paired tablet typically has no
  // multipass cookie.
  const kioskToken = readKioskToken()
  if (kioskToken) {
    headers['Authorization'] = `Kiosk ${kioskToken}`
    const browserId = readKioskBrowserId()
    if (browserId) {
      headers['X-Kiosk-Browser-Id'] = browserId
    }
  }
  // Per-tab impersonation: when this tab entered via "Access school/chef view",
  // re-send the session + banner tokens so the server resolves the request as
  // the impersonated org (auth middleware + /me prefer these headers).
  const impersonationSession = readImpersonationSession()
  if (impersonationSession) {
    headers['X-Impersonation-Session'] = impersonationSession
    const impersonationBanner = readImpersonationBanner()
    if (impersonationBanner) {
      headers['X-Impersonation-Banner'] = impersonationBanner
    }
  }
  return headers
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query)
  const headers: Record<string, string> = authHeaders()
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers,
  }
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body)
  }
  const res = await fetch(url, init)
  // 204 No Content is uncommon today but harmless to support: skip the
  // JSON parse so a future endpoint that wants to return "ok, no body"
  // works without a special-case here.
  if (res.status === 204) {
    return undefined as T
  }
  const text = await res.text()
  let parsed: unknown = null
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = { message: text }
    }
  }
  if (!res.ok) {
    const body = (parsed ?? { error: 'unknown', message: `HTTP ${res.status}` }) as ApiErrorBody
    throw new ApiError(res.status, body)
  }
  return parsed as T
}

export interface ApiFileResponse {
  blob: Blob
  /** Server-supplied name from `Content-Disposition`, when present. */
  filename: string | null
  /** Response headers, for endpoints that describe the file (e.g. `X-Packet-Pages`). */
  headers: Headers
}

export interface ApiFetchBlobOptions extends ApiFetchOptions {
  /** Gzip the JSON body when the browser supports `CompressionStream` (large uploads on cellular). */
  gzip?: boolean
  /** Reject a 2xx whose `Content-Type` is not this — a `202` JSON body is not the file. */
  expectContentType?: string
}

async function gzipJson(value: unknown): Promise<Blob | null> {
  if (typeof CompressionStream === 'undefined') {
    return null
  }
  const stream = new Blob([JSON.stringify(value)]).stream().pipeThrough(new CompressionStream('gzip'))
  return new Response(stream).blob()
}

/**
 * `apiFetch`'s binary sibling — same session cookie, same kiosk/impersonation
 * headers, but resolves the response as a `Blob` plus its download filename
 * (apiFetch always JSON-parses, so it can't serve file downloads).
 *
 * Use this for every authenticated file download: a top-level navigation
 * (`window.open`, `<a href>`) cannot carry the header-based tokens, so an
 * impersonated admin would silently download as themselves.
 */
export async function apiFetchBlob(path: string, opts: ApiFetchBlobOptions = {}): Promise<ApiFileResponse> {
  const headers: Record<string, string> = authHeaders()
  let body: BodyInit | undefined
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    const gzipped = opts.gzip ? await gzipJson(opts.body) : null
    if (gzipped) {
      headers['Content-Encoding'] = 'gzip'
      body = gzipped
    } else {
      body = JSON.stringify(opts.body)
    }
  }
  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers,
    body,
  })
  if (!res.ok) {
    // Errors still come back as the standard JSON envelope.
    const text = await res.text()
    let parsed: unknown
    try {
      parsed = text.length > 0 ? JSON.parse(text) : null
    } catch {
      parsed = { message: text }
    }
    throw new ApiError(res.status, (parsed ?? { error: 'unknown', message: `HTTP ${res.status}` }) as ApiErrorBody)
  }
  if (opts.expectContentType && !(res.headers.get('Content-Type') ?? '').startsWith(opts.expectContentType)) {
    throw new ApiError(res.status, { error: 'unexpected_response', message: 'The file did not come back.' })
  }
  const disposition = res.headers.get('Content-Disposition')
  const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null
  return { blob: await res.blob(), filename: match?.[1] ?? null, headers: res.headers }
}

/** Save a fetched blob to disk via a synthetic link (repo download idiom). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
